import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, X } from 'lucide-react';

import { useTournamentsQuery } from '@/services/endpoints/tournaments';
import { useAppSelector } from '@/store/hooks';
import { useHaptics } from '@/hooks/useHaptics';
import { cdnImage } from '@/lib/cdnImage';
import { cn } from '@/lib/cn';

/**
 * Watch Party entry button — only renders when at least one auto-handled
 * tournament is currently STARTED or IN_PROGRESS for the active game.
 * Polls every 4 s, mirroring mobile-app behaviour. Tap opens an inline modal
 * listing the currently-live tournaments.
 */
export function WatchPartyHub() {
  const activeGames = useAppSelector((s) => s.core.activeGames);
  const haptics = useHaptics();
  const [open, setOpen] = useState(false);

  const { data } = useTournamentsQuery(
    {
      limit: 10,
      games: activeGames.length ? activeGames.join(',') : undefined,
      tournament_type: '1,2,3,4,5,6,7,9,12',
    },
    { pollingInterval: 4000 },
  );

  const liveTournaments = useMemo(() => {
    return (
      data?.data.filter(
        (tour) =>
          tour.is_autohandled &&
          (tour.status === 'STARTED' || tour.status === 'IN_PROGRESS'),
      ) ?? []
    );
  }, [data]);

  if (liveTournaments.length === 0) return null;

  const onOpen = () => {
    haptics.impact('light');
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        className={cn(
          'relative flex h-11 items-center gap-2 overflow-hidden rounded-lg border-2 border-[#FF7B7B] px-4',
          'bg-[radial-gradient(circle_at_center,#AE2907_10%,#DB6262_100%)]',
          'text-xs font-bold uppercase tracking-wide text-white',
          'transition-opacity active:opacity-80',
          'shadow-[inset_0_0_10px_2px_#AE2907]',
        )}
      >
        <Eye aria-hidden className="h-5 w-5" />
        <span>Watch Party</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex flex-col bg-bg/95 backdrop-blur">
          <header className="flex items-center justify-between border-b border-border px-4 py-3">
            <div>
              <h2 className="text-base font-bold text-brand">Ongoing Watch Party</h2>
              <p className="mt-0.5 text-xs text-fg-muted">
                Watch battles live and learn from top players!
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-bg-secondary text-fg transition-opacity active:opacity-80"
            >
              <X aria-hidden className="h-5 w-5" />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto p-4">
            <ul className="flex flex-col gap-3">
              {liveTournaments.map((tour) => (
                <li
                  key={tour.id}
                  className={cn(
                    'flex items-center gap-3 rounded-xl border border-[#ED304C] p-3',
                    'shadow-[inset_0_0_30px_0_rgba(237,48,76,0.35)]',
                  )}
                >
                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-bg-tertiary">
                    {tour.image && (
                      <img
                        src={cdnImage(tour.image)}
                        alt=""
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    )}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <p className="truncate text-sm font-bold text-white">{tour.title}</p>
                    <Link
                      to={`/tournaments/${tour.id}/watch-party`}
                      onClick={() => setOpen(false)}
                      className="inline-flex items-center justify-center gap-1.5 rounded-md bg-[#ED304C] px-3 py-1.5 text-[11px] font-bold uppercase text-white transition-opacity active:opacity-80"
                    >
                      <Eye aria-hidden className="h-4 w-4" />
                      Watch the matches
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}
